import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { tap } from 'rxjs';
import {
  DatasourceTableSettingsCsvOptionsPropertyName,
  DatasourceTableSettingsDialogComponent,
  DatasourceTableSettingsDialogData,
  DatasourceTableSettingsDialogResult,
  DatasourceTableSettingsWriteSettingsPropertyName,
} from '../shared/datasource-table-settings-dialog';
import { DatasourceTableSettingsBase, DatasourceUIModel } from '../state';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DatasourceTableSettingsCheckService {
  constructor(private readonly dialog: MatDialog) {}

  async checkDatasourceTable<T extends DatasourceTableSettingsBase = DatasourceTableSettingsBase>(
    tableName: string,
    tableSettings: T,
    datasource: DatasourceUIModel,
  ): Promise<T> {
    const result = await this.checkDatasourceTables(new Map<string, T>([[tableName, tableSettings]]), datasource);
    return result.get(tableName)!;
  }

  checkDatasourceTables<T extends DatasourceTableSettingsBase = DatasourceTableSettingsBase>(
    tableSettings: Map<string, T>,
    datasource: DatasourceUIModel,
  ): Promise<Map<string, T>> {
    return new Promise<Map<string, T>>((resolve) => {
      const requiredEditTableSettings = new Map<string, T>();
      const passingTableSettings = new Map<string, T>();
      tableSettings.forEach((tableSettingsItem, key) => {
        if (this.checkIfHasSettings(tableSettingsItem)) {
          requiredEditTableSettings.set(key, tableSettingsItem);
        } else {
          passingTableSettings.set(key, tableSettingsItem);
        }
      });
      if (requiredEditTableSettings.size === 0) {
        resolve(tableSettings);
        return;
      }

      const clonedRequiredTableSettings = cloneDeep(Array.from(requiredEditTableSettings));
      const dialogRef = this.dialog.open<
        DatasourceTableSettingsDialogComponent,
        DatasourceTableSettingsDialogData,
        DatasourceTableSettingsDialogResult
      >(DatasourceTableSettingsDialogComponent, {
        data: {
          tableSettings: clonedRequiredTableSettings,
          activeDatasourceSettings: datasource,
        },
        panelClass: ['table-settings-panel'],
      });

      dialogRef
        .afterClosed()
        .pipe(
          tap((data: DatasourceTableSettingsDialogResult | undefined) => {
            if (!data) {
              return;
            }

            const resultMap = new Map<string, T>([
              ...Array.from(passingTableSettings),
              ...Array.from(requiredEditTableSettings),
            ]);

            data.tableSettings.forEach(([key, tableSettingsItem]) => {
              const currentSettings = resultMap.get(key);
              resultMap.set(key, merge(cloneDeep(currentSettings), tableSettingsItem));
            });

            resolve(resultMap);
          }),
          untilDestroyed(this),
        )
        .subscribe();
    });
  }

  checkIfHasSettings<T extends DatasourceTableSettingsBase>(settings: T | undefined): boolean {
    return !!(
      settings &&
      (settings[DatasourceTableSettingsCsvOptionsPropertyName] ||
        settings[DatasourceTableSettingsWriteSettingsPropertyName])
    );
  }
}
